/**
 * LegalProfileSettings.js
 * 
 * Komponente für Tenant Legal Profile + Branding Settings
 * Wird in Einstellungen.js eingebunden
 * 
 * Features:
 * - Pflichtfelder für Belege (Firmenname, Adresse, Steuer-Nr, etc.)
 * - Optionale Felder (Website, HRB, Öffnungszeiten)
 * - Branding (Logo, Template-Auswahl, Footer-Text)
 * - Vollständigkeits-Check
 * - Live-Vorschau
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Building2, Save, AlertTriangle, CheckCircle2, 
  Upload, Image, FileText, Eye, Loader2,
  Phone, Mail, Globe, MapPin, Hash, User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Field configuration for legal profile
const LEGAL_FIELDS = {
  required: [
    { key: 'company_name', label: 'Firmenname / Restaurantname', icon: Building2, placeholder: 'z.B. Zozo Burger GmbH' },
    { key: 'address_street', label: 'Straße & Hausnummer', icon: MapPin, placeholder: 'Musterstraße 123' },
    { key: 'address_zip', label: 'PLZ', icon: Hash, placeholder: '12345', width: '100px' },
    { key: 'address_city', label: 'Stadt', icon: MapPin, placeholder: 'Berlin' },
    { key: 'phone', label: 'Telefon', icon: Phone, placeholder: '+49 30 123456', type: 'tel' },
    { key: 'email', label: 'E-Mail', icon: Mail, placeholder: 'info@restaurant.de', type: 'email' },
    { key: 'tax_number', label: 'Steuernummer', icon: Hash, placeholder: '12/345/67890', hint: 'Pflicht für Kassenbelege' },
  ],
  optional: [
    { key: 'vat_id', label: 'USt-ID', icon: Hash, placeholder: 'DE123456789', hint: 'Optional (für B2B)' },
    { key: 'owner', label: 'Geschäftsführer / Inhaber', icon: User, placeholder: 'Max Mustermann' },
    { key: 'hrb', label: 'Handelsregister', icon: FileText, placeholder: 'HRB 12345 B' },
    { key: 'website', label: 'Website', icon: Globe, placeholder: 'www.restaurant.de', type: 'url' },
    { key: 'opening_hours', label: 'Öffnungszeiten', icon: FileText, placeholder: 'Mo-Fr 11-22, Sa-So 12-23', multiline: true },
  ]
};

export default function LegalProfileSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    legal_profile: {},
    branding: {},
    is_complete: false
  });
  const [completenessCheck, setCompletenessCheck] = useState({ is_complete: false, missing_fields: [] });
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const sessionToken = localStorage.getItem('access_token');

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/tenant/profile`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        if (data.branding?.logo_url) {
          setLogoPreview(`${BACKEND_URL}${data.branding.logo_url}`);
        }
        checkCompleteness();
      } else {
        console.error('Failed to load profile');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const checkCompleteness = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tenant/completeness-check`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompletenessCheck(data);
      }
    } catch (error) {
      console.error('Error checking completeness:', error);
    }
  };

  const updateLegalField = (key, value) => {
    setProfile(prev => ({
      ...prev,
      legal_profile: {
        ...prev.legal_profile,
        [key]: value
      }
    }));
  };

  const updateBrandingField = (key, value) => {
    setProfile(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        [key]: value
      }
    }));
  };

  const saveLegalProfile = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/tenant/legal-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profile.legal_profile)
      });

      if (response.ok) {
        toast.success('Legal Profile gespeichert!');
        checkCompleteness();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving legal profile:', error);
      toast.error('Netzwerkfehler');
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/tenant/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profile.branding)
      });

      if (response.ok) {
        toast.success('Branding gespeichert!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Netzwerkfehler');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Bitte ein Bild hochladen (PNG, JPEG, WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max 5MB)');
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/tenant/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setLogoPreview(`${BACKEND_URL}${data.logo_url}`);
        updateBrandingField('logo_url', data.logo_url);
        toast.success('Logo hochgeladen!');
      } else {
        toast.error('Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Netzwerkfehler');
    } finally {
      setUploadingLogo(false);
    }
  };

  const loadReceiptPreview = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/tenant/receipt-preview?template=${profile.branding?.receipt_template || 'standard'}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` }}
      );
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-6)', display: 'flex', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" size={24} />
        <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>Lade Profil...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      {/* Completeness Banner */}
      <div style={{
        padding: 'var(--space-4)',
        background: completenessCheck.is_complete 
          ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)'
          : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
        border: `1px solid ${completenessCheck.is_complete ? 'rgba(34, 197, 94, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`,
        borderRadius: 'var(--radius-lg)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)'
      }}>
        {completenessCheck.is_complete ? (
          <CheckCircle2 size={24} style={{ color: '#22C55E' }} />
        ) : (
          <AlertTriangle size={24} style={{ color: '#F59E0B' }} />
        )}
        <div style={{ flex: 1 }}>
          <strong style={{ color: 'var(--text-primary)', display: 'block' }}>
            {completenessCheck.is_complete 
              ? 'Legal Profile vollständig ✓' 
              : `${completenessCheck.missing_fields?.length || 0} Pflichtfelder fehlen`}
          </strong>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {completenessCheck.is_complete 
              ? 'Alle Pflichtangaben für Kassenbelege sind ausgefüllt.'
              : `Fehlend: ${completenessCheck.missing_fields?.map(f => f.label).join(', ')}`}
          </span>
        </div>
        <span style={{ 
          background: completenessCheck.is_complete ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 191, 36, 0.2)',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: completenessCheck.is_complete ? '#22C55E' : '#F59E0B'
        }}>
          {completenessCheck.filled || 0}/{completenessCheck.total_required || 7}
        </span>
      </div>

      {/* Legal Profile Section */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        border: '1px solid var(--border-primary)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Building2 size={20} style={{ color: '#3B82F6' }} />
              Pflichtangaben (Legal Profile)
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Diese Daten erscheinen auf allen Belegen und sind gesetzlich vorgeschrieben.
            </p>
          </div>
          <button
            onClick={saveLegalProfile}
            disabled={saving}
            data-testid="save-legal-profile-btn"
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: saving ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              color: saving ? 'var(--text-secondary)' : 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-4)' }}>
          {LEGAL_FIELDS.required.map(field => (
            <div key={field.key} style={{ width: field.width || '100%' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '0.875rem', 
                fontWeight: 500, 
                color: 'var(--text-primary)', 
                marginBottom: 'var(--space-2)' 
              }}>
                <field.icon size={14} style={{ color: '#3B82F6' }} />
                {field.label}
                <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type={field.type || 'text'}
                value={profile.legal_profile?.[field.key] || ''}
                onChange={(e) => updateLegalField(field.key, e.target.value)}
                placeholder={field.placeholder}
                data-testid={`legal-${field.key}`}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  background: 'var(--bg-primary)',
                  border: `1px solid ${!profile.legal_profile?.[field.key] ? 'rgba(251, 191, 36, 0.5)' : 'var(--border-primary)'}`,
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              />
              {field.hint && (
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  {field.hint}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Optional fields */}
        <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-primary)' }}>
          <h4 style={{ margin: '0 0 var(--space-3)', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Optionale Angaben
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-4)' }}>
            {LEGAL_FIELDS.optional.map(field => (
              <div key={field.key}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  fontSize: '0.875rem', 
                  fontWeight: 500, 
                  color: 'var(--text-primary)', 
                  marginBottom: 'var(--space-2)' 
                }}>
                  <field.icon size={14} style={{ color: 'var(--text-secondary)' }} />
                  {field.label}
                </label>
                {field.multiline ? (
                  <textarea
                    value={profile.legal_profile?.[field.key] || ''}
                    onChange={(e) => updateLegalField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={profile.legal_profile?.[field.key] || ''}
                    onChange={(e) => updateLegalField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem'
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Branding Section */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        border: '1px solid var(--border-primary)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Image size={20} style={{ color: '#8B5CF6' }} />
              Branding & Templates
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Logo, Bon-Template und Footer-Text für alle Belege.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              onClick={loadReceiptPreview}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-primary)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-primary)'
              }}
            >
              <Eye size={14} />
              Vorschau
            </button>
            <button
              onClick={saveBranding}
              disabled={saving}
              data-testid="save-branding-btn"
              style={{
                padding: 'var(--space-2) var(--space-4)',
                background: saving ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                color: saving ? 'var(--text-secondary)' : 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Speichern
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
          {/* Logo Upload */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)', display: 'block' }}>
              Logo
            </label>
            <div style={{
              border: '2px dashed var(--border-primary)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-4)',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--bg-primary)',
              transition: 'all 0.2s'
            }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
                id="logo-upload"
                disabled={uploadingLogo}
              />
              <label htmlFor="logo-upload" style={{ cursor: 'pointer' }}>
                {logoPreview ? (
                  <div>
                    <img 
                      src={logoPreview} 
                      alt="Logo" 
                      style={{ maxWidth: '180px', maxHeight: '80px', objectFit: 'contain', marginBottom: '8px' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>
                      Klicken zum Ändern
                    </p>
                  </div>
                ) : (
                  <div>
                    {uploadingLogo ? (
                      <Loader2 size={32} className="animate-spin" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }} />
                    ) : (
                      <Upload size={32} style={{ color: 'var(--text-secondary)', marginBottom: '8px' }} />
                    )}
                    <p style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 500 }}>Logo hochladen</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                      PNG, JPEG, WebP (max 5MB)
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Template & Footer */}
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            {/* Receipt Template */}
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)', display: 'block' }}>
                Bon-Template
              </label>
              <select
                value={profile.branding?.receipt_template || 'standard'}
                onChange={(e) => updateBrandingField('receipt_template', e.target.value)}
                data-testid="receipt-template-select"
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              >
                <option value="standard">📄 Standard (80mm - vollständig mit Logo)</option>
                <option value="compact">📋 Compact (58mm - minimal, ohne Logo)</option>
              </select>
            </div>

            {/* Footer Text */}
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: 'var(--space-2)', display: 'block' }}>
                Bon-Footer Text
              </label>
              <input
                type="text"
                value={profile.branding?.footer_text || ''}
                onChange={(e) => updateBrandingField('footer_text', e.target.value)}
                placeholder="Vielen Dank für Ihren Besuch!"
                data-testid="footer-text-input"
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* Show Logo Checkboxes */}
            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={profile.branding?.show_logo_on_receipt !== false}
                  onChange={(e) => updateBrandingField('show_logo_on_receipt', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                Logo auf Kassenbon
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={profile.branding?.show_logo_on_kitchen === true}
                  onChange={(e) => updateBrandingField('show_logo_on_kitchen', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                Logo auf Küchenbon
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Preview Modal */}
      {showPreview && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPreview(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-4)',
              maxWidth: '400px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1F2937' }}>Bon-Vorschau</h3>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  color: '#6B7280'
                }}
              >
                ×
              </button>
            </div>
            <div 
              style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px' }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
